import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ShieldCheck, LogOut } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { TeamStatsTiles } from '../components/TeamStatsTiles';
import { fetchAccountTeamStats } from '../api/analytics';
import { ApiError } from '../api/client';
import { TeamStats } from '../types/curling';

/**
 * Deliberately standalone, not part of the Curling Codex app shell: a sponsor never
 * receives raw game or shot data (enforced server-side, see
 * AccountAccessService.fullyReadableAccountIds), so this page has no links to Game
 * History, Teams, Rock Map, or anything else that assumes full detail — just the
 * condensed team-stats tiles the Dashboard already shows everyone else.
 */
export function SponsorView() {
  const { user, activeAccountId, activeAccount, setActiveAccountId, signOut } = useAuth();
  const navigate = useNavigate();

  const [teamStatsList, setTeamStatsList] = useState<TeamStats[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!activeAccountId) return;
    let cancelled = false;

    setLoading(true);
    setError('');
    fetchAccountTeamStats(activeAccountId)
      .then(list => {
        if (cancelled) return;
        setTeamStatsList(list);
        setSelectedTeam(current => (list.some(t => t.teamName === current) ? current : list[0]?.teamName ?? ''));
      })
      .catch(caught => {
        if (cancelled) return;
        setError(caught instanceof ApiError ? caught.message : 'Could not load stats.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeAccountId]);

  async function handleSignOut() {
    await signOut();
    navigate('/', { replace: true });
  }

  const selectedStats = teamStatsList.find(t => t.teamName === selectedTeam) ?? null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-blue-300" />
              Sponsor View
            </h1>
            <p className="text-blue-300">
              Condensed results and trends{activeAccount ? ` for ${activeAccount.accountName}` : ''}.
            </p>
          </div>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>

        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6">
          <div className="flex flex-wrap gap-4 items-center mb-6">
            {user && user.accounts.length > 1 && (
              <div className="flex items-center gap-3">
                <label className="text-sm text-blue-300">Program</label>
                <select
                  value={activeAccountId ?? ''}
                  onChange={e => setActiveAccountId(e.target.value)}
                  className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                >
                  {user.accounts.map(account => (
                    <option key={account.accountId} value={account.accountId} className="bg-blue-950 text-white">
                      {account.accountName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {teamStatsList.length > 1 && (
              <div className="flex items-center gap-3">
                <label className="text-sm text-blue-300">Team</label>
                <select
                  value={selectedTeam}
                  onChange={e => setSelectedTeam(e.target.value)}
                  className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                >
                  {teamStatsList.map(t => (
                    <option key={t.teamName} value={t.teamName} className="bg-blue-950 text-white">
                      {t.teamName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {loading ? (
            <p className="text-blue-300">Loading…</p>
          ) : error ? (
            <p className="text-red-400">{error}</p>
          ) : selectedStats ? (
            <TeamStatsTiles teamStats={selectedStats} />
          ) : (
            <p className="text-blue-300">No games recorded yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
